/**
 * How the React chat talks to the CRM (ADR 0021): the console's own Supabase
 * JWT, the Account in X-Account-Id (and in the body), gateway calls through
 * the CRM's pass-through (/crm/proxy/v1/...) so the CRM keeps applying its
 * permissions and writing operator attribution, CRM-local calls at /crm/v1/...
 */
import { api } from '../api/client';
import { LOGGED_OUT_MESSAGE, isLoggedOutEnvelope, noteGatewayEnvelope } from '../crm/accountSession';
import { CRM_FRAME_PATH } from '../crm/crmBridge';

export class CrmError extends Error {
  status: number;
  code: string | null;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    const b = body as { code?: unknown } | null;
    this.code = b && typeof b.code === 'string' ? b.code : null;
  }
}

/** The first readable message in a gateway/CRM error body (CRM ui.js extractGatewayError order). */
export function errorMessage(body: unknown, fallback: string): string {
  if (isLoggedOutEnvelope(body)) return LOGGED_OUT_MESSAGE;
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const d = (b.data && typeof b.data === 'object' ? b.data : {}) as Record<string, unknown>;
  const candidates = [
    (d.error as { message?: unknown } | undefined)?.message,
    d.detail,
    d.message,
    typeof b.data === 'string' ? b.data : null,
    b.detail,
    typeof b.error === 'string' ? b.error : (b.error as { message?: unknown } | undefined)?.message,
    b.message,
  ];
  const hit = candidates.find((c) => typeof c === 'string' && c.trim());
  return hit ? String(hit) : fallback;
}

function isExpiredToken(body: unknown): boolean {
  const detail = (body as { detail?: unknown } | null)?.detail;
  return typeof detail === 'string' && /invalid or expired token/i.test(detail);
}

export interface CrmRequest {
  accountId: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: Record<string, unknown> | FormData;
  signal?: AbortSignal;
  /** Gateway pass-through (default) or a route the CRM serves itself. */
  local?: boolean;
}

async function send(path: string, req: CrmRequest, token: string | null): Promise<Response> {
  const isForm = typeof FormData !== 'undefined' && req.body instanceof FormData;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (req.accountId) headers['X-Account-Id'] = req.accountId;
  let body: BodyInit | undefined;
  if (isForm) body = req.body as FormData;
  else if (req.body) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({ account_id: req.accountId, ...(req.body as Record<string, unknown>) });
  }
  const prefix = req.local ? CRM_FRAME_PATH.replace(/\/$/, '') : `${CRM_FRAME_PATH}proxy`;
  return fetch(`${prefix}${path}`, {
    method: req.method || (req.body ? 'POST' : 'GET'),
    headers,
    body,
    cache: 'no-store',
    signal: req.signal,
  });
}

/**
 * Call the CRM and return the parsed JSON body. Throws CrmError when the HTTP
 * status is not OK or the body says ok:false. A stale token is refreshed once.
 */
export async function crmFetch<T = unknown>(path: string, req: CrmRequest): Promise<T> {
  let res = await send(path, req, api.getToken());
  let body: unknown = await res.json().catch(() => null);
  if (res.status === 401 && (req.local || isExpiredToken(body))) {
    const fresh = await api.refreshAccessToken();
    if (fresh) {
      res = await send(path, req, fresh);
      body = await res.json().catch(() => null);
    }
  }
  if (!req.local && res.ok) noteGatewayEnvelope(req.accountId, body);
  if (!res.ok || (body as { ok?: unknown } | null)?.ok === false) {
    throw new CrmError(errorMessage(body, `Request failed (${res.status})`), res.status, body);
  }
  return body as T;
}

/** Raw response (for byte endpoints such as /v1/media/cdn-fetch), with the same auth handling. */
export async function crmFetchRaw(path: string, req: CrmRequest): Promise<Response> {
  let res = await send(path, { ...req, local: true }, api.getToken());
  if (res.status === 401) {
    const fresh = await api.refreshAccessToken();
    if (fresh) res = await send(path, { ...req, local: true }, fresh);
  }
  return res;
}

/** Raw response from the gateway pass-through or (with `local`) a CRM route, with the same auth handling. */
export async function crmResponse(path: string, req: CrmRequest): Promise<Response> {
  let res = await send(path, req, api.getToken());
  if (res.status === 401) {
    const body: unknown = await res.clone().json().catch(() => null);
    if (req.local || isExpiredToken(body)) {
      const fresh = await api.refreshAccessToken();
      if (fresh) res = await send(path, req, fresh);
    }
  }
  if (!req.local && res.ok && /json/i.test(res.headers.get('content-type') || '')) {
    noteGatewayEnvelope(req.accountId, await res.clone().json().catch(() => null));
  }
  return res;
}

/** `data` of a gateway/CRM envelope ({ok, data}), else the body itself. */
export function dataOf<T = Record<string, unknown>>(body: unknown): T {
  const b = body as { data?: unknown } | null;
  return (b && b.data !== undefined ? b.data : body) as T;
}

/** First array among the usual list keys of a gateway payload. */
export function listOf(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) return payload as Record<string, unknown>[];
  const p = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
  for (const key of ['list', 'items', 'users', 'messages', 'data']) {
    if (Array.isArray(p[key])) return p[key] as Record<string, unknown>[];
  }
  if (p.list && typeof p.list === 'object') return Object.values(p.list) as Record<string, unknown>[];
  return [];
}
